import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BasicInfoTab = ({ profile, onSave }) => {
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    role: profile?.role || '',
    experience: profile?.experience || '',
    location: profile?.location || '',
    website: profile?.website || '',
    bio: profile?.bio || '',
    languages: profile?.languages || []
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const roleOptions = [
    { value: 'agent', label: 'Agent Musical' },
    { value: 'booker', label: 'Booker' },
    { value: 'promoter', label: 'Promoteur' },
    { value: 'venue_owner', label: 'Propriétaire de Venue' },
    { value: 'event_organizer', label: 'Organisateur d\'Événements' }
  ];

  const experienceOptions = [
    { value: 'beginner', label: 'Débutant (0-2 ans)' },
    { value: 'intermediate', label: 'Intermédiaire (2-5 ans)' },
    { value: 'experienced', label: 'Expérimenté (5-10 ans)' },
    { value: 'expert', label: 'Expert (10+ ans)' }
  ];

  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'Anglais' },
    { value: 'es', label: 'Espagnol' },
    { value: 'de', label: 'Allemand' },
    { value: 'it', label: 'Italien' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: profile?.name || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      role: profile?.role || '',
      experience: profile?.experience || '',
      location: profile?.location || '',
      website: profile?.website || '',
      bio: profile?.bio || '',
      languages: profile?.languages || []
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Informations de Base</h2>
        {!isEditing ? (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
          >
            Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Sauvegarder
            </Button>
          </div>
        )}
      </div>
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Nom complet"
          type="text"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          disabled={!isEditing}
          required
        />

        <Input
          label="Email"
          type="email"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          disabled={!isEditing}
          required
        />

        <Input
          label="Téléphone"
          type="tel"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          disabled={!isEditing}
        />

        <Select
          label="Rôle professionnel"
          options={roleOptions}
          value={formData?.role}
          onChange={(value) => handleInputChange('role', value)}
          disabled={!isEditing}
          required
        />

        <Select
          label="Niveau d'expérience"
          options={experienceOptions}
          value={formData?.experience}
          onChange={(value) => handleInputChange('experience', value)}
          disabled={!isEditing}
        />

        <Input
          label="Localisation"
          type="text"
          value={formData?.location}
          onChange={(e) => handleInputChange('location', e?.target?.value)}
          disabled={!isEditing}
          placeholder="Ville, Pays"
        />

        <Input
          label="Site web"
          type="url"
          value={formData?.website}
          onChange={(e) => handleInputChange('website', e?.target?.value)}
          disabled={!isEditing}
          placeholder="https://..."
        />

        <Select
          label="Langues parlées"
          options={languageOptions}
          value={formData?.languages}
          onChange={(value) => handleInputChange('languages', value)}
          disabled={!isEditing}
          multiple
          searchable
        />
      </div>
      {/* Bio Section */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Biographie professionnelle
        </label>
        <textarea
          value={formData?.bio}
          onChange={(e) => handleInputChange('bio', e?.target?.value)}
          disabled={!isEditing}
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground resize-none"
          placeholder="Décrivez votre expérience et vos spécialités dans l'industrie musicale..."
        />
      </div>
      {/* Contact Verification Status */}
      <div className="bg-muted rounded-lg p-4">
        <h3 className="font-medium text-foreground mb-3">Statut de vérification</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm text-foreground">Email vérifié</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={16} className="text-warning" />
            <span className="text-sm text-foreground">Téléphone en attente de vérification</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoTab;